import { rem, TmElement } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { IconName } from '@thiememeulenhoff/types';
import { ToasterType } from './toaster.types';
import {
  tag,
  toasterMessageStyles,
  ToasterStyles,
} from './toaster-message.styles';

@customElement(tag)
export class ToasterMessageComponent extends TmElement {
  static override styles = toasterMessageStyles;

  private static currentToastStack: HTMLElement;

  private static get toastStack() {
    if (!this.currentToastStack) {
      const stack = document.createElement('a-toaster-stack');
      stack.classList.add('float');
      this.currentToastStack = Object.assign(stack);
      document.body.append(ToasterMessageComponent.toastStack);
    }
    return this.currentToastStack;
  }

  @property({ type: String, reflect: true })
  type: ToasterType = 'neutral';

  @property({ type: String, reflect: true })
  icon?: IconName;

  @property({ type: Boolean, reflect: true })
  dismissable?: boolean;

  @property({ type: Number })
  set duration(duration: number) {
    if (this.#timout) {
      clearTimeout(this.#timout);
    }

    if (duration) {
      this.#timout = window.setTimeout(
        () => this.dismiss.bind(this)(),
        duration,
      );
    }
  }

  #timout?: number;

  override render() {
    return html`
      <div class="icon-wrapper">${this.#iconTemplate()}</div>
      <div class="content-wrapper">${this.#contentTemplate()}</div>
    `;
  }

  #iconTemplate() {
    return html`<a-icon
      class="toaster-icon-${this.type}"
      name="${this.icon || ToasterStyles[this.type].icon.name}"
    ></a-icon>`;
  }

  #contentTemplate() {
    return html`
      <div class="header">
        <h3 data-test="header"><slot name="header"></slot></h3>
        <a-icon-button
          data-test="close-button"
          @click=${this.dismiss}
          class="close-button"
          icon="close"
          icon-size="small"
          size="small"
          variant="ghost"
        ></a-icon-button>
      </div>
      <p data-test="body"><slot name="body"></slot></p>
    `;
  }

  toast() {
    this.classList.add('move-in');

    ToasterMessageComponent.toastStack.appendChild(this);
  }

  dismiss(event?: Event) {
    event?.stopPropagation();

    this.style.setProperty('--calculated-height', `${rem(this.clientHeight)}`);
    this.classList.add('dismissing');
    this.addEventListener('animationend', this.#animationEnd);
    this.dispatchLocalEvent({ eventName: 'dismissing' });
  }

  #animationEnd = (event: AnimationEvent) => {
    switch (event.animationName) {
      case 'moveRight':
        this.#handleMoveRightEnd();
        break;
      case 'shrink':
        this.#handleShrinkEnd();
        break;
      default:
    }
  };

  #handleMoveRightEnd = () => {
    this.removeEventListener('animationend', this.#handleMoveRightEnd);
    this.classList.add('shrink');
    this.addEventListener('animationend', this.#handleShrinkEnd);
  };

  #handleShrinkEnd = () => {
    this.classList.remove('shrink');
    this.removeEventListener('animationend', this.#handleShrinkEnd);
    this.#dismissed();
  };

  #dismissed() {
    this.dispatchLocalEvent({ eventName: 'dismissed' });
    this.remove();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toaster-message': ToasterMessageComponent;
  }
}
